import { useEffect, useState } from "react";
import api from "../../lib/api";

type Provider = "openai" | "gemini";

type AiBlogConfig = {
  keywords: string;
  scheduleTime: string;
  autoEnabled: boolean;
  provider: Provider;
  model: string;
};

type AiBlogPost = {
  id: number;
  source: "manual" | "auto" | string;
  provider?: Provider | string;
  model?: string | null;
  createdAt: string;
  keywords: string;
  content: string;
};

const DEFAULT_CONFIG: AiBlogConfig = {
  keywords: "임대료, 현금결제",
  scheduleTime: "09:50",
  autoEnabled: false,
  provider: "gemini",
  model: "gemini-2.5-flash",
};

function prettyDate(s?: string | null) {
  if (!s) return "-";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString("ko-KR");
}

function downloadText(filename: string, content: string) {
  const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function AiBlogSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [hasGeminiKey, setHasGeminiKey] = useState(false);
  const [hasOpenAIKey, setHasOpenAIKey] = useState(false);
  const [postCount, setPostCount] = useState(0);
  const [lastPostAt, setLastPostAt] = useState<string | null>(null);
  const [config, setConfig] = useState<AiBlogConfig>(DEFAULT_CONFIG);
  const [posts, setPosts] = useState<AiBlogPost[]>([]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [statusRes, postsRes] = await Promise.all([
        api.get("/admin/ai-blog/status"),
        api.get("/admin/ai-blog/posts", { params: { limit: 50 } }),
      ]);

      const statusData = statusRes.data || {};
      const postsData = postsRes.data || {};

      setHasGeminiKey(!!statusData.hasGeminiKey);
      setHasOpenAIKey(!!statusData.hasOpenAIKey);
      setPostCount(Number(statusData.postCount || 0));
      setLastPostAt(statusData.lastPostAt || null);

      const nextConfig = statusData.config || {};
      setConfig({
        keywords: nextConfig.keywords ?? DEFAULT_CONFIG.keywords,
        scheduleTime: nextConfig.scheduleTime ?? DEFAULT_CONFIG.scheduleTime,
        autoEnabled: !!nextConfig.autoEnabled,
        provider:
          String(nextConfig.provider || "").toLowerCase() === "gemini"
            ? "gemini"
            : "openai",
        model: String(nextConfig.model || DEFAULT_CONFIG.model),
      });

      setPosts(Array.isArray(postsData.items) ? postsData.items : []);
    } catch (e: any) {
      console.error(e);
      setError(e?.response?.data?.message || "AI 블로그 설정을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const saveConfig = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = {
        keywords: config.keywords,
        scheduleTime: config.scheduleTime,
        autoEnabled: config.autoEnabled,
        provider: config.provider,
        model: config.model,
      };
      await api.put("/admin/ai-blog/config", payload);
      setSuccess("설정이 저장되었습니다.");
      await load();
    } catch (e: any) {
      console.error(e);
      setError(e?.response?.data?.message || "설정 저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const generateNow = async () => {
    setGenerating(true);
    setError(null);
    setSuccess(null);
    try {
      await api.post("/admin/ai-blog/generate");
      setSuccess("글을 생성했습니다.");
      await load();
    } catch (e: any) {
      console.error(e);
      setError(e?.response?.data?.message || "글 생성에 실패했습니다.");
    } finally {
      setGenerating(false);
    }
  };

  const downloadAll = () => {
    const all = posts
      .map((post) => {
        return [
          "==============================",
          `생성시각: ${prettyDate(post.createdAt)}`,
          `실행방식: ${post.source === "auto" ? "자동" : "수동"}`,
          `모델: ${post.provider || "-"} / ${post.model || "-"}`,
          `키워드: ${post.keywords || "-"}`,
          "==============================",
          post.content || "",
          "",
        ].join("\n");
      })
      .join("\n");

    downloadText("ai_blog_posts.txt", all);
  };

  const modelPlaceholder =
    config.provider === "openai" ? "예: gpt-4.1-mini" : "예: gemini-2.5-flash";

  const providerReady = config.provider === "openai" ? hasOpenAIKey : hasGeminiKey;

  if (loading) {
    return <div className="text-sm text-slate-500">AI 블로그 설정을 불러오는 중...</div>;
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold text-slate-900">AI 블로그 자동 생성</h1>
        <p className="mt-1 text-sm text-slate-500">
          키워드와 시간을 설정하면 서버가 매일 KST 기준으로 글을 자동 생성합니다.
        </p>
      </div>

      <div className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 md:grid-cols-2">
        <div className="rounded-lg bg-slate-50 p-3 text-sm">
          <div className="font-medium text-slate-700">API 상태</div>
          <div className="mt-1 text-slate-600">OpenAI: {hasOpenAIKey ? "연결됨" : "미설정"}</div>
          <div className="text-slate-600">Gemini: {hasGeminiKey ? "연결됨" : "미설정"}</div>
        </div>
        <div className="rounded-lg bg-slate-50 p-3 text-sm">
          <div className="font-medium text-slate-700">현재 상태</div>
          <div className="mt-1 text-slate-600">
            자동 생성: {config.autoEnabled ? "활성화" : "비활성화"}
          </div>
          <div className="text-slate-600">생성 글 수: {postCount.toLocaleString("ko-KR")}개</div>
          <div className="text-slate-600">마지막 생성: {prettyDate(lastPostAt)}</div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-800">키워드</label>
            <input
              type="text"
              value={config.keywords}
              onChange={(e) => setConfig((prev) => ({ ...prev, keywords: e.target.value }))}
              placeholder="예: 임대료, 현금결제"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-800">자동 생성 시간 (KST)</label>
            <input
              type="time"
              value={config.scheduleTime}
              onChange={(e) => setConfig((prev) => ({ ...prev, scheduleTime: e.target.value }))}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-800">AI 제공자</label>
            <select
              value={config.provider}
              onChange={(e) =>
                setConfig((prev) => ({
                  ...prev,
                  provider: e.target.value === "gemini" ? "gemini" : "openai",
                }))
              }
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value="openai">OpenAI</option>
              <option value="gemini">Gemini</option>
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-800">모델명</label>
            <input
              type="text"
              value={config.model}
              onChange={(e) => setConfig((prev) => ({ ...prev, model: e.target.value }))}
              placeholder={modelPlaceholder}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <input
            id="autoEnabled"
            type="checkbox"
            checked={config.autoEnabled}
            onChange={(e) => setConfig((prev) => ({ ...prev, autoEnabled: e.target.checked }))}
            className="h-4 w-4 rounded border-slate-300 text-indigo-600"
          />
          <label htmlFor="autoEnabled" className="text-sm text-slate-700">
            자동 생성 활성화
          </label>
        </div>

        {!providerReady && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
            선택한 제공자 API 키가 서버에 설정되지 않았습니다.
          </div>
        )}

        {error && (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {error}
          </div>
        )}

        {success && (
          <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
            {success}
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={saveConfig}
            disabled={saving}
            className="inline-flex h-9 items-center justify-center rounded-lg bg-indigo-600 px-4 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? "저장 중..." : "설정 저장"}
          </button>
          <button
            type="button"
            onClick={generateNow}
            disabled={generating}
            className="inline-flex h-9 items-center justify-center rounded-lg bg-slate-900 px-4 text-sm font-semibold text-white hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {generating ? "생성 중..." : "지금 생성하기"}
          </button>
          <button
            type="button"
            onClick={downloadAll}
            disabled={!posts.length}
            className="inline-flex h-9 items-center justify-center rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            전체 다운로드
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-900">
            생성된 글 ({posts.length.toLocaleString("ko-KR")}개)
          </h2>
        </div>

        {!posts.length && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
            아직 생성된 글이 없습니다.
          </div>
        )}

        <div className="space-y-3">
          {posts.map((post) => (
            <article key={post.id} className="rounded-lg border border-slate-200 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-xs text-slate-500">
                  {prettyDate(post.createdAt)} · {post.source === "auto" ? "자동" : "수동"} ·{" "}
                  {(post.provider || "-").toString().toUpperCase()} / {post.model || "-"}
                </div>
                <button
                  type="button"
                  onClick={() => downloadText(`ai_blog_post_${post.id}.txt`, post.content || "")}
                  className="inline-flex h-8 items-center justify-center rounded-lg border border-slate-300 bg-white px-3 text-xs font-medium text-slate-700 hover:bg-slate-50"
                >
                  다운로드
                </button>
              </div>
              <div className="mt-1 text-xs text-indigo-600">키워드: {post.keywords || "-"}</div>
              <pre className="mt-3 whitespace-pre-wrap break-words text-sm leading-6 text-slate-800">
                {post.content}
              </pre>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
